// frontend/src/lib/api/authService.ts
// ✅ FIXED: Removed breaking changes and TypeScript errors

import { BaseApiService } from './baseService';
import { httpClient, ApiResponse } from '../httpClient';
import { 
  User, 
  EmailVerificationStatus, 
  LoginCredentials, 
  RegisterData,
  AuthResult 
} from '@/types/auth';
import { ApiResponse as TypedApiResponse } from '@/types/api';

export interface AuthResponse {
  user: User;
  token: string;
  refresh_token?: string;
  expires_in?: number;
  email_verification?: EmailVerificationStatus;
}

export interface PasswordResetData {
  email: string;
}

export interface PasswordResetConfirmData {
  token: string;
  password: string;
  password_confirm: string;
}

export interface ChangePasswordData {
  current_password: string;
  password: string;
  password_confirm: string;
}

export interface RefreshTokenData {
  refresh_token: string;
}

export interface EmailCheckData {
  email: string;
}

export interface EmailCheckResponse {
  available: boolean;
  exists: boolean;
  message?: string;
}

class AuthService extends BaseApiService {
  constructor() {
    super({
      baseEndpoint: '/auth',
      timeout: 10000,
    });
  }

  // ✅ PUBLIC ROUTES: These should NEVER trigger token refresh on 401

  async login(credentials: LoginCredentials): Promise<ApiResponse<AuthResponse>> {
    // ✅ EXPLICIT: Mark as public route - no auth header, no refresh on 401
    return this.post<AuthResponse>('/login', credentials, { skipAuth: true });
  }

  async registerCustomer(data: RegisterData): Promise<ApiResponse<AuthResponse>> {
    const registrationData = {
      ...data,
      terms_accepted: data.terms_accepted ? 1 : 0,
      marketing_consent: data.marketing_consent ? 1 : 0,
    };
    return this.post<AuthResponse>('/register/customer', registrationData, { skipAuth: true });
  }

  async registerPartner(data: RegisterData): Promise<ApiResponse<AuthResponse>> {
    const registrationData = {
      ...data,
      terms_accepted: data.terms_accepted ? 1 : 0,
      background_check_consent: data.background_check_consent ? 1 : 0,
      marketing_consent: data.marketing_consent ? 1 : 0,
    };
    return this.post<AuthResponse>('/register/partner', registrationData, { skipAuth: true });
  }

  async requestPasswordReset(data: PasswordResetData): Promise<ApiResponse<{ message: string }>> {
    return this.post<{ message: string }>('/forgot-password', data, { skipAuth: true });
  }

  async confirmPasswordReset(data: PasswordResetConfirmData): Promise<ApiResponse<{ message: string }>> {
    return this.post<{ message: string }>('/reset-password', data, { skipAuth: true });
  }

  async verifyEmail(token: string): Promise<ApiResponse<{ 
    user?: User; 
    email_verification?: EmailVerificationStatus;
    message: string;
  }>> {
    return this.post<{ 
      user?: User; 
      email_verification?: EmailVerificationStatus;
      message: string;
    }>('/verify-email', { token }, { skipAuth: true });
  }

  async checkEmailAvailability(data: EmailCheckData): Promise<ApiResponse<EmailCheckResponse>> {
    // Use absolute path to bypass base endpoint for this public API
    // Note: httpClient already has /api as base URL, so we just need /public/...
    const config = {
      method: 'POST' as const,
      url: '/public/check-email',
      data,
      timeout: this.timeout,
      headers: { 
        'Content-Type': 'application/json',
        skipAuth: 'true' 
      }
    };
    
    try {
      const response = await httpClient.request(config);
      return response.data;
    } catch (error: any) {
      console.error('API POST /public/check-email failed:', error);
      
      if (error.response?.data) {
        return error.response.data;
      }
      
      return {
        success: false,
        message: error.message || 'Email check failed',
      };
    }
  }

  async refreshToken(data: RefreshTokenData): Promise<ApiResponse<AuthResponse>> {
    // ✅ SPECIAL: Refresh token is public but uses refresh token, not access token
    return this.post<AuthResponse>('/refresh', data, { skipAuth: true });
  }

  // ✅ PROTECTED ROUTES: These require authentication and can trigger refresh

  async getCurrentUser(): Promise<ApiResponse<{ user: User }>> {
    // ✅ PROTECTED: Should have auth header and can trigger refresh on 401
    return this.get<{ user: User }>('/me');
  }

  async logout(): Promise<ApiResponse<{ message: string }>> {
    // ✅ PROTECTED: Requires auth token
    return this.post<{ message: string }>('/logout');
  }

  async changePassword(data: ChangePasswordData): Promise<ApiResponse<{ message: string }>> {
    return this.put<{ message: string }>('/change-password', data);
  }

  async updateProfile(data: Partial<User>): Promise<ApiResponse<{ user: User }>> {
    return this.put<{ user: User }>('/profile', data);
  }

  async uploadProfileImage(file: File): Promise<ApiResponse<{ user: User; profile_image_url: string }>> {
    const formData = new FormData();
    formData.append('profile_image', file);
    return this.post<{ user: User; profile_image_url: string }>('/upload-avatar', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  }

  async resendVerificationEmail(): Promise<ApiResponse<{ message: string }>> {
    // ✅ SEMI-PROTECTED: Usually requires auth, but depends on implementation
    return this.post<{ message: string }>('/resend-verification');
  }

  async getEmailVerificationStatus(): Promise<ApiResponse<EmailVerificationStatus>> {
    return this.get<EmailVerificationStatus>('/verification-status');
  }

  // ✅ UTILITY METHODS

  async checkEmailExists(email: string): Promise<{ exists: boolean; message?: string }> {
    console.log('checkEmailExists called with:', email);
    try {
      console.log('Calling checkEmailAvailability...');
      const response = await this.checkEmailAvailability({ email });
      console.log('checkEmailAvailability response:', response);
      
      if (response.success && response.data) {
        const result = { 
          exists: !response.data.available,
          message: response.message 
        };
        console.log('checkEmailExists returning:', result);
        return result;
      }
      console.log('checkEmailExists: No success/data, returning exists: false');
      return { exists: false };
    } catch (error: any) {
      console.warn('Email check failed, assuming email does not exist:', error);
      return { exists: false };
    }
  }

  async getActiveSessions(): Promise<ApiResponse<any[]>> {
    return this.get<any[]>('/sessions');
  }

  async revokeSession(sessionId: string): Promise<ApiResponse<{ message: string }>> {
    return this.delete<{ message: string }>(`/sessions/${sessionId}`);
  }

  async revokeAllSessions(): Promise<ApiResponse<{ message: string }>> {
    return this.delete<{ message: string }>('/sessions');
  }

  // ✅ FIXED: Use proper delete request without data property in RequestOptions
  async deleteAccount(password: string): Promise<ApiResponse<{ message: string }>> {
    // ✅ For DELETE requests with body, use request method directly
    return this.request<{ message: string }>('DELETE', '/account', { password });
  }
}

export const authService = new AuthService();

// Export standalone functions for backward compatibility
export const login = (credentials: LoginCredentials) => authService.login(credentials);
export const registerCustomer = (data: RegisterData) => authService.registerCustomer(data);
export const registerPartner = (data: RegisterData) => authService.registerPartner(data);
export const getCurrentUser = () => authService.getCurrentUser();
export const logout = () => authService.logout();
export const refreshToken = (data: RefreshTokenData) => authService.refreshToken(data);
export const requestPasswordReset = (data: PasswordResetData) => authService.requestPasswordReset(data);
export const confirmPasswordReset = (data: PasswordResetConfirmData) => authService.confirmPasswordReset(data);
export const changePassword = (data: ChangePasswordData) => authService.changePassword(data);
export const updateProfile = (data: Partial<User>) => authService.updateProfile(data);
export const uploadProfileImage = (file: File) => authService.uploadProfileImage(file);
export const verifyEmail = (token: string) => authService.verifyEmail(token);
export const resendVerificationEmail = () => authService.resendVerificationEmail();
export const getEmailVerificationStatus = () => authService.getEmailVerificationStatus();
export const checkEmailAvailability = (data: EmailCheckData) => authService.checkEmailAvailability(data);

// ✅ FIX: Export the standalone function for direct import
export const checkEmailExists = async (email: string): Promise<{ exists: boolean; message?: string }> => {
  return authService.checkEmailExists(email);
};

export default authService;